/// <amd-module name="Feed/Legacy/Actions/FeedSettings/NavigateToViewsHandler" />
import { Feed, FeedPermission, FeedView } from "Feed/Common/Contracts/Feed";
import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";

/**
 * When user navigates to feed settings -> Views pivot
 * 1. set view loading spinner
 * 2. get views from server
 * 3. get view permissions from server
 * 4. set data is fetched from server
 */
export class NavigateToViewsHandler {
    public static async handleAsync(state: IFeedSettingsState, emit: () => void): Promise<void> {
        state.isLoadingViews = true;
        state.isLoadingViewPermissions = true;
        emit();

        const feedsDataService: IFeedDataService = state.pageContext.getService<IFeedDataService>("IFeedDataService");
        const feed = state.feed();
        const projectId = feed.project ? feed.project.id : null;

        return feedsDataService
            .getFeedViewsAsync(projectId, feed.id)
            .then(
                (views: FeedView[]) => {
                    state.isLoadingViews = false;
                    state.views = views;
                    emit();
                },
                (reason: Error) => {
                    state.isLoadingViews = false;
                    throw reason;
                }
            )
            .then(() => {
                const viewPermissionsPromises: Array<Promise<ViewAndPermissions>> = [];
                state.views.forEach((view: FeedView) => {
                    viewPermissionsPromises.push(
                        NavigateToViewsHandler.getViewAndPermissionsAsync(feedsDataService, state.feed(), view.id)
                    );
                });
                return Promise.all(viewPermissionsPromises);
            })
            .then(
                (viewAndPermissions: ViewAndPermissions[]) => {
                    state.viewPermissions = {};
                    for (const vp of viewAndPermissions) {
                        state.viewPermissions[vp.viewId] = vp.permissions;
                    }
                    state.isLoadingViewPermissions = false;
                    state.isViewsDataLoadedFromServer = true;
                    emit();
                },
                (reason: Error) => {
                    state.isLoadingViewPermissions = false;
                    throw reason;
                }
            );
    }

    private static async getViewAndPermissionsAsync(
        feedsDataService: IFeedDataService,
        feed: Feed,
        viewId: string
    ): Promise<ViewAndPermissions> {
        return feedsDataService
            .getFeedViewPermissionsAsync(feed, viewId, true /*includeIds*/, true /*excludeInheritedPermissions*/)
            .then((permissions: FeedPermission[]) => {
                return Promise.resolve({ viewId, permissions });
            });
    }
}

class ViewAndPermissions {
    public viewId: string;
    public permissions: FeedPermission[];
}
